import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { WellbeingData } from '../../../types/wellbeing';
import { JointIcon } from '../CategoryIcons';

interface ExtraIntestinalModalProps {
  data: WellbeingData;
  onSave: (data: Partial<WellbeingData>) => void;
  onClose: () => void;
}

export const ExtraIntestinalModal: React.FC<ExtraIntestinalModalProps> = ({ data, onSave, onClose }) => {
  const [localData, setLocalData] = useState({
    jointPain: data.jointPain ?? 0,
    erythemaNodosum: data.erythemaNodosum ?? false,
    pyodermaGangrenosum: data.pyodermaGangrenosum ?? false,
    skinOther: data.skinOther ?? false,
    skinOtherNote: data.skinOtherNote ?? '',
    eyeProblems: data.eyeProblems ?? false,
    mouthSores: data.mouthSores ?? false,
  });

  const Slider = ({
    label,
    value,
    onChange
  }: {
    label: string;
    value: number;
    onChange: (val: number) => void;
  }) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '6px',
      }}>
        <label style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>
          {label}
        </label>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#6B7FFF' }}>
          {value}/5
        </span>
      </div>
      <input
        type="range"
        min="0"
        max="5"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        style={{
          width: '100%',
          height: '6px',
          cursor: 'pointer',
          accentColor: '#6B7FFF',
        }}
      />
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '20px',
        paddingTop: '60px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '20px',
          padding: '24px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          position: 'relative',
          marginBottom: '40px',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <JointIcon />
          </div>

          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#0f172a',
            margin: 0,
          }}>
            Objawy pozajelitowe
          </h2>
        </div>

        <Slider
          label="Ból stawów"
          value={localData.jointPain}
          onChange={(v) => setLocalData({ ...localData, jointPain: v })}
        />

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px',
            borderRadius: '10px',
            border: '2px solid #e2e8f0',
            cursor: 'pointer',
            background: localData.erythemaNodosum ? '#f0f4ff' : 'white',
          }}>
            <input
              type="checkbox"
              checked={localData.erythemaNodosum}
              onChange={(e) => setLocalData({ ...localData, erythemaNodosum: e.target.checked })}
              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#6B7FFF' }}
            />
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#0f172a' }}>Rumień guzowaty</span>
          </label>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px',
            borderRadius: '10px',
            border: '2px solid #e2e8f0',
            cursor: 'pointer',
            background: localData.pyodermaGangrenosum ? '#f0f4ff' : 'white',
          }}>
            <input
              type="checkbox"
              checked={localData.pyodermaGangrenosum}
              onChange={(e) => setLocalData({ ...localData, pyodermaGangrenosum: e.target.checked })}
              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#6B7FFF' }}
            />
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#0f172a' }}>Piodermia zgorzelinowa</span>
          </label>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px',
            borderRadius: '10px',
            border: '2px solid #e2e8f0',
            cursor: 'pointer',
            background: localData.skinOther ? '#f0f4ff' : 'white',
          }}>
            <input
              type="checkbox"
              checked={localData.skinOther}
              onChange={(e) => setLocalData({
                ...localData,
                skinOther: e.target.checked,
                skinOtherNote: e.target.checked ? localData.skinOtherNote : '',
              })}
              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#6B7FFF' }}
            />
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#0f172a' }}>Inne</span>
          </label>
          {localData.skinOther && (
            <input
              type="text"
              value={localData.skinOtherNote}
              onChange={(e) => setLocalData({ ...localData, skinOtherNote: e.target.value })}
              placeholder="Co dokładnie? (wymagane)"
              style={{
                width: '100%',
                marginTop: 8,
                padding: 10,
                fontSize: 14,
                borderRadius: 8,
                border: '2px solid #e2e8f0',
                outline: 'none',
              }}
            />
          )}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px',
            borderRadius: '10px',
            border: '2px solid #e2e8f0',
            cursor: 'pointer',
            background: localData.eyeProblems ? '#f0f4ff' : 'white',
          }}>
            <input
              type="checkbox"
              checked={localData.eyeProblems}
              onChange={(e) => setLocalData({ ...localData, eyeProblems: e.target.checked })}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#6B7FFF',
              }}
            />
            <span style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#0f172a',
            }}>
              Problemy z oczami
            </span>
          </label>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px',
            borderRadius: '10px',
            border: '2px solid #e2e8f0',
            cursor: 'pointer',
            background: localData.mouthSores ? '#f0f4ff' : 'white',
          }}>
            <input
              type="checkbox"
              checked={localData.mouthSores}
              onChange={(e) => setLocalData({ ...localData, mouthSores: e.target.checked })}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#6B7FFF',
              }}
            />
            <span style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#0f172a',
            }}>
              Afty w ustach
            </span>
          </label>
        </div>

        <button
          onClick={() => {
            if (localData.skinOther && !localData.skinOtherNote?.trim()) {
              toast.error('Wpisz, co dokładnie (np. rodzaj zmian skórnych)');
              return;
            }
            onSave(localData);
          }}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '10px',
            border: 'none',
            background: 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
            color: 'white',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Gotowe ✓
        </button>
      </div>
    </div>
  );
};
